import { useEffect, useState } from "react";
import { CheckCircle2, Clock, Mail } from "lucide-react";
import { Dialog, DialogContent, DialogDescription, DialogTitle } from "@/components/ui/dialog";
import WhatsAppIcon from "@/components/icons/WhatsAppIcon";

export interface QuoteSent {
  ticketId?: string;
  name?: string;
  email?: string;
}

/*
 * The "thank you" pop-up after a quote request is sent. It lives once in App,
 * not in the form: the Get Free Quote dialog closes (and its form goes) the
 * moment the request is sent, and this has to stay until the visitor closes it.
 */
let listener: ((sent: QuoteSent) => void) | null = null;
export const showQuoteSent = (sent: QuoteSent) => listener?.(sent);

const QuoteSentDialog = () => {
  const [sent, setSent] = useState<QuoteSent | null>(null);
  useEffect(() => {
    listener = setSent;
    return () => {
      listener = null;
    };
  }, []);

  const first = sent?.name?.trim().split(/\s+/)[0];
  return (
    <Dialog open={!!sent} onOpenChange={(open) => !open && setSent(null)}>
      <DialogContent className="w-[calc(100%-2rem)] max-w-md gap-0 overflow-hidden rounded-2xl border-none bg-[#FDFCF7] p-0 shadow-2xl sm:rounded-2xl [&>button:last-child]:text-white [&>button:last-child]:opacity-90 [&>button:last-child>svg]:h-6 [&>button:last-child>svg]:w-6">
        <div className="flex flex-col items-center bg-[#2C4E5A] px-6 pb-6 pt-8 text-center text-white">
          <span className="flex h-16 w-16 items-center justify-center rounded-full bg-white/15 ring-4 ring-white/10">
            <CheckCircle2 className="h-10 w-10 text-[#7DDBA3]" strokeWidth={2.2} />
          </span>
          <DialogTitle className="mt-4 font-poppins text-2xl font-bold leading-tight text-white">
            Thank you{first ? `, ${first}` : ""}!
          </DialogTitle>
          <DialogDescription className="mt-1.5 text-[15px] text-white/85">
            Your request has been received.
          </DialogDescription>
          {sent?.ticketId && (
            <p className="mt-4 rounded-full bg-white/10 px-4 py-1.5 text-sm">
              Reference ID: <b className="font-semibold tracking-wide">{sent.ticketId}</b>
            </p>
          )}
        </div>

        <div className="space-y-3.5 px-6 py-6 text-[15px] leading-relaxed text-[#2C4E5A]">
          <p className="flex gap-3">
            <Mail className="mt-0.5 h-5 w-5 shrink-0 text-[#C24E00]" />
            <span>
              A confirmation email is on its way{sent?.email ? <> to <b className="break-words font-semibold">{sent.email}</b></> : ""}. If you don't see it,
              please check your spam folder.
            </span>
          </p>
          <p className="flex gap-3">
            <Clock className="mt-0.5 h-5 w-5 shrink-0 text-[#C24E00]" />
            <span>Our Ayurveda care team will contact you within 24–48 hours.</span>
          </p>
        </div>

        {/* stacked full width on phones (no flex-1 there: in a column it would squash them to their text) */}
        <div className="flex flex-col gap-3 px-6 pb-7 sm:flex-row">
          <a
            href="https://wa.me/919156115572"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex h-12 w-full shrink-0 items-center justify-center gap-2 rounded-xl bg-[#25D366] text-base font-semibold text-white transition-colors hover:bg-[#1EBE5A] sm:w-auto sm:flex-1"
          >
            <WhatsAppIcon className="h-5 w-5" /> Chat on WhatsApp
          </a>
          <button
            type="button"
            onClick={() => setSent(null)}
            className="inline-flex h-12 w-full shrink-0 items-center justify-center rounded-xl bg-[#C24E00] text-base font-semibold text-white transition-colors hover:bg-[#A84300] sm:w-auto sm:flex-1"
          >
            Close
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default QuoteSentDialog;
